import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getMeetings, addMeeting, updateMeeting, deleteMeeting } from '../lib/supabase';
import { NAVY, GOLD, GOLD_LIGHT, WHITE, BG } from '../lib/ui';
import { Avatar, Input, Select, Textarea, Field, Btn } from '../lib/ui';
import Modal from './Modal';

const fmt     = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtTime = t => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h);
  return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};
const today   = () => new Date().toISOString().slice(0, 10);
const DAYS    = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function MeetingPill({ meeting, isOwn, onClick }) {
  return (
    <div onClick={e => { e.stopPropagation(); onClick(meeting); }} style={{
      background: isOwn ? NAVY : GOLD + '33',
      color: isOwn ? GOLD : NAVY,
      borderRadius: 4, padding: '2px 6px', fontSize: 11, fontWeight: 600,
      cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      marginBottom: 2, border: isOwn ? 'none' : `1px solid ${GOLD}55`,
    }}>
      {fmtTime(meeting.start_time)} {meeting.title}
    </div>
  );
}

function MeetingDetailModal({ meeting, team, matters, onClose, onEdit, onDelete }) {
  const memberName = id => team.find(m => m.id === id)?.name || '—';
  const matter = matters.find(m => m.id === meeting.matter_id);
  return (
    <Modal title={meeting.title} onClose={onClose}>
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        {/* Date & time */}
        <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
          <div style={{ background:'#F7F5F0', borderRadius:8, padding:'10px 14px', flex:1 }}>
            <div style={{ fontSize:11, color:'#999', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>Date</div>
            <div style={{ fontSize:14, fontWeight:600, color:NAVY }}>{fmt(meeting.meeting_date)}</div>
          </div>
          <div style={{ background:'#F7F5F0', borderRadius:8, padding:'10px 14px', flex:1 }}>
            <div style={{ fontSize:11, color:'#999', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>Time</div>
            <div style={{ fontSize:14, fontWeight:600, color:NAVY }}>{fmtTime(meeting.start_time)}{meeting.end_time ? ` — ${fmtTime(meeting.end_time)}` : ''}</div>
          </div>
        </div>

        {/* Video link */}
        {meeting.video_link && (
          <div style={{ background: NAVY, borderRadius:8, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
            <div>
              <div style={{ fontSize:11, color:GOLD_LIGHT, marginBottom:2 }}>Meeting Link</div>
              <div style={{ fontSize:13, color:WHITE, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:300 }}>{meeting.video_link}</div>
            </div>
            <a href={meeting.video_link} target="_blank" rel="noopener noreferrer" style={{ background:GOLD, color:NAVY, borderRadius:6, padding:'8px 16px', fontSize:12, fontWeight:700, textDecoration:'none', flexShrink:0 }}>
              Join ↗
            </a>
          </div>
        )}

        {/* Location */}
        {meeting.location && (
          <div>
            <div style={{ fontSize:11, color:'#999', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>Location</div>
            <div style={{ fontSize:13, color:NAVY }}>{meeting.location}</div>
          </div>
        )}

        {/* Description */}
        {meeting.description && (
          <div>
            <div style={{ fontSize:11, color:'#999', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>Description</div>
            <div style={{ fontSize:13, color:'#555', lineHeight:1.7 }}>{meeting.description}</div>
          </div>
        )}

        {/* Matter */}
        {matter && (
          <div>
            <div style={{ fontSize:11, color:'#999', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>Linked Matter</div>
            <div style={{ fontSize:13, color:NAVY, fontWeight:600 }}>{matter.name}</div>
          </div>
        )}

        {/* Attendees */}
        <div>
          <div style={{ fontSize:11, color:'#999', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>Attendees ({(meeting.attendees||[]).length})</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {(meeting.attendees||[]).map(id => {
              const m = team.find(t => t.id === id);
              if (!m) return null;
              return (
                <div key={id} style={{ display:'flex', alignItems:'center', gap:6, background:'#F7F5F0', borderRadius:20, padding:'4px 10px' }}>
                  <Avatar name={m.name} size={20} />
                  <span style={{ fontSize:12, color:NAVY }}>{m.name}</span>
                </div>
              );
            })}
            {(meeting.attendees||[]).length === 0 && <span style={{ fontSize:13, color:'#BBB' }}>No attendees specified</span>}
          </div>
        </div>

        {/* Notes */}
        {meeting.notes && (
          <div>
            <div style={{ fontSize:11, color:'#999', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>Meeting Notes</div>
            <div style={{ fontSize:13, color:'#555', lineHeight:1.7, background:'#FFFBF0', border:`1px solid ${GOLD}44`, borderRadius:8, padding:'10px 14px' }}>{meeting.notes}</div>
          </div>
        )}

        <div style={{ display:'flex', gap:8, justifyContent:'space-between', borderTop:'1px solid #F0EDE8', paddingTop:16 }}>
          <button onClick={() => { if(window.confirm('Delete this meeting?')) onDelete(meeting.id); }} style={{ background:'none', border:'1px solid #C0392B', color:'#C0392B', borderRadius:6, padding:'7px 14px', cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>Delete</button>
          <div style={{ display:'flex', gap:8 }}>
            <Btn variant="ghost" onClick={onClose}>Close</Btn>
            <Btn onClick={() => onEdit(meeting)}>Edit Meeting</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function MeetingFormModal({ meeting, team, matters, currentUserName, onClose, onSave }) {
  const isEdit = !!meeting?.id;
  const [form, setForm] = useState({
    title: '', description: '', meeting_date: today(), start_time: '10:00',
    end_time: '11:00', location: '', video_link: '', attendees: [],
    matter_id: '', notes: '', reminder_min: 30, created_by: currentUserName,
    ...(meeting || {})
  });
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setForm(f => ({
    ...f, attendees: f.attendees.includes(id) ? f.attendees.filter(x => x !== id) : [...f.attendees, id]
  }));

  const handleSave = async () => {
    if (!form.title || !form.meeting_date || !form.start_time) return;
    setSaving(true);
    const data = { ...form, matter_id: form.matter_id || null };
    await onSave(data);
    setSaving(false);
  };

  return (
    <Modal title={isEdit ? 'Edit Meeting' : 'New Meeting'} onClose={onClose} wide>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <Field label="Meeting Title" col="1 / -1">
          <Input value={form.title} onChange={e => setForm({...form, title:e.target.value})} placeholder="e.g. AgaOne onboarding call" />
        </Field>
        <Field label="Date">
          <Input type="date" value={form.meeting_date} onChange={e => setForm({...form, meeting_date:e.target.value})} />
        </Field>
        <Field label="Linked Matter">
          <Select value={form.matter_id||''} onChange={e => setForm({...form, matter_id:e.target.value})}>
            <option value="">No linked matter</option>
            {matters.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Select>
        </Field>
        <Field label="Start Time">
          <Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time:e.target.value})} />
        </Field>
        <Field label="End Time">
          <Input type="time" value={form.end_time||''} onChange={e => setForm({...form, end_time:e.target.value})} />
        </Field>
        <Field label="Video / Call Link" col="1 / -1">
          <Input value={form.video_link||''} onChange={e => setForm({...form, video_link:e.target.value})} placeholder="https://zoom.us/j/... or Teams link" />
        </Field>
        <Field label="Location">
          <Input value={form.location||''} onChange={e => setForm({...form, location:e.target.value})} placeholder="Office / Zoom / etc." />
        </Field>
        <Field label="Reminder">
          <Select value={form.reminder_min} onChange={e => setForm({...form, reminder_min:parseInt(e.target.value)})}>
            <option value={0}>No reminder</option>
            <option value={15}>15 minutes before</option>
            <option value={30}>30 minutes before</option>
            <option value={60}>1 hour before</option>
            <option value={1440}>1 day before</option>
          </Select>
        </Field>
        <Field label="Description" col="1 / -1">
          <Textarea value={form.description||''} onChange={e => setForm({...form, description:e.target.value})} placeholder="Agenda or context…" />
        </Field>
        <Field label="Attendees" col="1 / -1">
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', paddingTop:4 }}>
            {team.map(m => {
              const on = (form.attendees||[]).includes(m.id);
              return (
                <label key={m.id} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', background:on?NAVY+'11':'transparent', borderRadius:20, padding:'5px 12px', border:on?`1px solid ${NAVY}33`:'1px solid #E0DADA' }}>
                  <input type="checkbox" style={{ display:'none' }} checked={on} onChange={() => toggle(m.id)} />
                  <Avatar name={m.name} size={20} />
                  <span style={{ fontWeight:on?700:400, color:on?NAVY:'#555' }}>{m.name}</span>
                </label>
              );
            })}
          </div>
        </Field>
        {isEdit && (
          <Field label="Meeting Notes" col="1 / -1">
            <Textarea rows={4} value={form.notes||''} onChange={e => setForm({...form, notes:e.target.value})} placeholder="Notes from the meeting…" />
          </Field>
        )}
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:20 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={handleSave} style={{ opacity: form.title && form.meeting_date ? 1 : 0.5 }}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Meeting'}
        </Btn>
      </div>
    </Modal>
  );
}

// ── MONTHLY VIEW ──────────────────────────────────────────────────────────────
function MonthView({ meetings, currentUserId, year, month, onDayClick, onMeetingClick }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = today();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const getMeetingsForDay = (d) => {
    if (!d) return [];
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    return meetings.filter(m => m.meeting_date === dateStr);
  };

  return (
    <div>
      {/* Day headers */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:1, marginBottom:1 }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:700, color:'#AAA', padding:'8px 0', textTransform:'uppercase', letterSpacing:0.5 }}>{d}</div>
        ))}
      </div>
      {/* Calendar grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:1, background:'#E5E0D8', border:'1px solid #E5E0D8', borderRadius:8, overflow:'hidden' }}>
        {cells.map((d, i) => {
          const dayMeetings = getMeetingsForDay(d);
          const dateStr = d ? `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}` : '';
          const isToday = dateStr === todayStr;
          return (
            <div key={i} onClick={() => d && onDayClick(dateStr)} style={{
              background: WHITE, minHeight:90, padding:6, cursor: d ? 'pointer' : 'default',
              opacity: d ? 1 : 0,
            }}
              onMouseEnter={e => d && (e.currentTarget.style.background = '#FAFAF8')}
              onMouseLeave={e => d && (e.currentTarget.style.background = WHITE)}>
              {d && (
                <>
                  <div style={{ fontSize:13, fontWeight: isToday ? 700 : 400, color: isToday ? WHITE : '#555', background: isToday ? NAVY : 'transparent', borderRadius:'50%', width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:4 }}>{d}</div>
                  {dayMeetings.slice(0,3).map(m => (
                    <MeetingPill key={m.id} meeting={m} isOwn={(m.attendees||[]).includes(currentUserId)} onClick={onMeetingClick} />
                  ))}
                  {dayMeetings.length > 3 && <div style={{ fontSize:10, color:'#AAA', paddingLeft:4 }}>+{dayMeetings.length - 3} more</div>}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── WEEKLY VIEW ───────────────────────────────────────────────────────────────
function WeekView({ meetings, currentUserId, weekStart, onMeetingClick, onDayClick }) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  const todayStr = today();

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:8 }}>
      {days.map((d, i) => {
        const dateStr = d.toISOString().slice(0,10);
        const dayMeetings = meetings.filter(m => m.meeting_date === dateStr);
        const isToday = dateStr === todayStr;
        return (
          <div key={i}>
            <div onClick={() => onDayClick(dateStr)} style={{ textAlign:'center', marginBottom:8, cursor:'pointer' }}>
              <div style={{ fontSize:11, color:'#AAA', textTransform:'uppercase', letterSpacing:0.5 }}>{DAYS[d.getDay()]}</div>
              <div style={{ fontSize:20, fontWeight:700, color: isToday ? WHITE : NAVY, background: isToday ? NAVY : 'transparent', borderRadius:'50%', width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', margin:'4px auto 0' }}>{d.getDate()}</div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6, minHeight:120 }}>
              {dayMeetings.map(m => {
                const isOwn = (m.attendees||[]).includes(currentUserId);
                return (
                  <div key={m.id} onClick={() => onMeetingClick(m)} style={{
                    background: isOwn ? NAVY : WHITE, color: isOwn ? GOLD_LIGHT : NAVY,
                    border: isOwn ? 'none' : `1px solid ${GOLD}55`,
                    borderLeft: `3px solid ${isOwn ? GOLD : GOLD}`,
                    borderRadius:8, padding:'8px 10px', cursor:'pointer',
                  }}>
                    <div style={{ fontSize:11, fontWeight:700, marginBottom:2 }}>{fmtTime(m.start_time)}</div>
                    <div style={{ fontSize:12, fontWeight:600, lineHeight:1.3 }}>{m.title}</div>
                    {m.video_link && <div style={{ fontSize:10, marginTop:4, opacity:0.7 }}>📹 Video link</div>}
                  </div>
                );
              })}
              {dayMeetings.length === 0 && (
                <div onClick={() => onDayClick(dateStr)} style={{ height:60, border:`1px dashed #E5E0D8`, borderRadius:8, cursor:'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = GOLD}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E0D8'} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── LIST VIEW ─────────────────────────────────────────────────────────────────
function ListView({ meetings, currentUserId, team, matters, onMeetingClick }) {
  const todayStr = today();
  const upcoming = meetings.filter(m => m.meeting_date >= todayStr);
  const past     = meetings.filter(m => m.meeting_date < todayStr);
  const memberName = id => team.find(t => t.id === id)?.name || '—';
  const matterName = id => matters.find(m => m.id === id)?.name || '';

  const MeetingRow = ({ m }) => {
    const isOwn = (m.attendees||[]).includes(currentUserId);
    return (
      <div onClick={() => onMeetingClick(m)} style={{
        background: WHITE, borderRadius:10, border:`1px solid ${isOwn ? NAVY+'33' : '#E5E0D8'}`,
        borderLeft:`4px solid ${isOwn ? NAVY : GOLD}`, padding:'14px 18px',
        display:'flex', gap:16, alignItems:'center', cursor:'pointer', marginBottom:8,
      }}
        onMouseEnter={e => e.currentTarget.style.boxShadow='0 2px 12px rgba(27,42,74,0.1)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
        {/* Date block */}
        <div style={{ textAlign:'center', minWidth:48, background: isOwn ? NAVY : '#F7F5F0', borderRadius:8, padding:'8px 6px' }}>
          <div style={{ fontSize:10, color: isOwn ? GOLD_LIGHT : '#AAA', textTransform:'uppercase', letterSpacing:0.5 }}>
            {MONTHS[new Date(m.meeting_date).getMonth()].slice(0,3)}
          </div>
          <div style={{ fontSize:22, fontWeight:700, color: isOwn ? GOLD : NAVY, lineHeight:1 }}>
            {new Date(m.meeting_date).getDate()}
          </div>
          <div style={{ fontSize:10, color: isOwn ? GOLD_LIGHT : '#AAA' }}>
            {new Date(m.meeting_date).getFullYear()}
          </div>
        </div>
        {/* Details */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:700, color:NAVY, marginBottom:4 }}>{m.title}</div>
          <div style={{ fontSize:12, color:'#888', display:'flex', gap:12, flexWrap:'wrap' }}>
            <span>⏰ {fmtTime(m.start_time)}{m.end_time ? ` — ${fmtTime(m.end_time)}` : ''}</span>
            {m.location && <span>📍 {m.location}</span>}
            {matterName(m.matter_id) && <span>📁 {matterName(m.matter_id)}</span>}
            {(m.attendees||[]).length > 0 && <span>👥 {(m.attendees||[]).map(memberName).join(', ')}</span>}
          </div>
        </div>
        {/* Join button */}
        {m.video_link && (
          <a href={m.video_link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ background:NAVY, color:GOLD, borderRadius:7, padding:'8px 14px', fontSize:12, fontWeight:700, textDecoration:'none', flexShrink:0 }}>
            Join ↗
          </a>
        )}
        {isOwn && !m.video_link && (
          <div style={{ background:GOLD+'22', color:GOLD, borderRadius:20, padding:'4px 10px', fontSize:11, fontWeight:700, flexShrink:0 }}>You</div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ fontSize:13, fontWeight:700, color:NAVY, marginBottom:10, fontFamily:'Georgia,serif' }}>Upcoming ({upcoming.length})</div>
      {upcoming.length === 0 && <div style={{ color:'#BBB', fontSize:13, padding:'16px 0', marginBottom:16 }}>No upcoming meetings.</div>}
      {upcoming.map(m => <MeetingRow key={m.id} m={m} />)}
      {past.length > 0 && (
        <>
          <div style={{ fontSize:13, fontWeight:700, color:'#AAA', margin:'20px 0 10px', fontFamily:'Georgia,serif' }}>Past ({past.length})</div>
          {past.slice(-5).reverse().map(m => <MeetingRow key={m.id} m={m} />)}
        </>
      )}
    </div>
  );
}

// ── MAIN CALENDAR VIEW ────────────────────────────────────────────────────────
export default function CalendarView({ team, matters, currentUserEmail }) {
  const [meetings,     setMeetings]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [calView,      setCalView]      = useState('month');
  const [year,         setYear]         = useState(new Date().getFullYear());
  const [month,        setMonth]        = useState(new Date().getMonth());
  const [weekStart,    setWeekStart]    = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d;
  });
  const [showForm,     setShowForm]     = useState(false);
  const [editMeeting,  setEditMeeting]  = useState(null);
  const [viewMeeting,  setViewMeeting]  = useState(null);
  const [newDatePre,   setNewDatePre]   = useState(null);

  const me = team.find(m => m.email === currentUserEmail);
  const currentUserId = me?.id || null;
  const currentUserName = me?.name || currentUserEmail;

  useEffect(() => {
    getMeetings().then(data => { setMeetings(data || []); setLoading(false); });
  }, []);

  const handleAdd = async (data) => {
    const m = await addMeeting(data);
    setMeetings(ms => [...ms, m].sort((a,b) => a.meeting_date.localeCompare(b.meeting_date) || a.start_time.localeCompare(b.start_time)));
    setShowForm(false); setEditMeeting(null); setNewDatePre(null);
  };
  const handleUpdate = async (data) => {
    const m = await updateMeeting(data.id, data);
    setMeetings(ms => ms.map(x => x.id === m.id ? m : x));
    setEditMeeting(null); setViewMeeting(null);
  };
  const handleDelete = async (id) => {
    await deleteMeeting(id);
    setMeetings(ms => ms.filter(m => m.id !== id));
    setViewMeeting(null);
  };

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y-1); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y+1); } else setMonth(m => m+1); };
  const prevWeek  = () => { const d = new Date(weekStart); d.setDate(d.getDate()-7); setWeekStart(d); };
  const nextWeek  = () => { const d = new Date(weekStart); d.setDate(d.getDate()+7); setWeekStart(d); };

  const onDayClick = (dateStr) => { setNewDatePre(dateStr); setShowForm(true); };
  const onMeetingClick = (m) => setViewMeeting(m);

  const myCount = meetings.filter(m => (m.attendees||[]).includes(currentUserId) && m.meeting_date >= today()).length;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:'Georgia,serif', fontSize:20, fontWeight:700, color:NAVY }}>Calendar</div>
          {myCount > 0 && <div style={{ fontSize:12, color:GOLD, marginTop:2 }}>You have {myCount} upcoming meeting{myCount!==1?'s':''}</div>}
        </div>
        <button onClick={() => { setNewDatePre(today()); setShowForm(true); }} style={{ background:NAVY, color:GOLD, border:'none', borderRadius:7, padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>+ New Meeting</button>
      </div>

      {/* View tabs + nav */}
      <div style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', padding:'12px 16px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        {/* Tab switcher */}
        <div style={{ display:'flex', gap:0, border:'1px solid #E5E0D8', borderRadius:7, overflow:'hidden' }}>
          {[['month','Month'],['week','Week'],['list','List']].map(([k,l]) => (
            <button key={k} onClick={() => setCalView(k)} style={{
              padding:'7px 16px', border:'none', background: calView===k ? NAVY : WHITE,
              color: calView===k ? GOLD : '#888', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'inherit'
            }}>{l}</button>
          ))}
        </div>

        {/* Navigation */}
        {calView === 'month' && (
          <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, justifyContent:'center' }}>
            <button onClick={prevMonth} style={{ background:'none', border:'1px solid #E5E0D8', borderRadius:6, padding:'5px 10px', cursor:'pointer', color:NAVY }}>‹</button>
            <span style={{ fontSize:14, fontWeight:700, color:NAVY, minWidth:140, textAlign:'center' }}>{MONTHS[month]} {year}</span>
            <button onClick={nextMonth} style={{ background:'none', border:'1px solid #E5E0D8', borderRadius:6, padding:'5px 10px', cursor:'pointer', color:NAVY }}>›</button>
            <button onClick={() => { setMonth(new Date().getMonth()); setYear(new Date().getFullYear()); }} style={{ background:'none', border:'1px solid #D0CBBF', borderRadius:6, padding:'5px 10px', cursor:'pointer', color:'#888', fontSize:11 }}>Today</button>
          </div>
        )}
        {calView === 'week' && (
          <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, justifyContent:'center' }}>
            <button onClick={prevWeek} style={{ background:'none', border:'1px solid #E5E0D8', borderRadius:6, padding:'5px 10px', cursor:'pointer', color:NAVY }}>‹</button>
            <span style={{ fontSize:13, fontWeight:700, color:NAVY, minWidth:180, textAlign:'center' }}>
              {weekStart.toLocaleDateString('en-GB',{day:'2-digit',month:'short'})} — {new Date(new Date(weekStart).setDate(weekStart.getDate()+6)).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}
            </span>
            <button onClick={nextWeek} style={{ background:'none', border:'1px solid #E5E0D8', borderRadius:6, padding:'5px 10px', cursor:'pointer', color:NAVY }}>›</button>
            <button onClick={() => { const d=new Date(); d.setDate(d.getDate()-d.getDay()); setWeekStart(d); }} style={{ background:'none', border:'1px solid #D0CBBF', borderRadius:6, padding:'5px 10px', cursor:'pointer', color:'#888', fontSize:11 }}>This Week</button>
          </div>
        )}

        {/* Legend */}
        <div style={{ display:'flex', gap:12, alignItems:'center', marginLeft:'auto' }}>
          <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#888' }}>
            <div style={{ width:10, height:10, borderRadius:2, background:NAVY }} />Your meetings
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#888' }}>
            <div style={{ width:10, height:10, borderRadius:2, background:GOLD+'55', border:`1px solid ${GOLD}` }} />Team meetings
          </div>
        </div>
      </div>

      {/* Calendar content */}
      <div style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', padding:16 }}>
        {loading ? (
          <div style={{ textAlign:'center', color:'#BBB', padding:40 }}>Loading meetings…</div>
        ) : calView === 'month' ? (
          <MonthView meetings={meetings} currentUserId={currentUserId} year={year} month={month} onDayClick={onDayClick} onMeetingClick={onMeetingClick} />
        ) : calView === 'week' ? (
          <WeekView meetings={meetings} currentUserId={currentUserId} weekStart={weekStart} onMeetingClick={onMeetingClick} onDayClick={onDayClick} />
        ) : (
          <ListView meetings={meetings} currentUserId={currentUserId} team={team} matters={matters} onMeetingClick={onMeetingClick} />
        )}
      </div>

      {/* Modals */}
      {(showForm || editMeeting) && (
        <MeetingFormModal
          meeting={editMeeting || (newDatePre ? { meeting_date: newDatePre } : null)}
          team={team} matters={matters} currentUserName={currentUserName}
          onClose={() => { setShowForm(false); setEditMeeting(null); setNewDatePre(null); }}
          onSave={editMeeting ? handleUpdate : handleAdd}
        />
      )}
      {viewMeeting && !editMeeting && (
        <MeetingDetailModal
          meeting={viewMeeting} team={team} matters={matters}
          onClose={() => setViewMeeting(null)}
          onEdit={(m) => { setEditMeeting(m); setViewMeeting(null); }}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
